export type ProfileStatusCode =
  | 'lead'
  | 'profile_completed'
  | 'video_pending'
  | 'qualified'
  | 'contacted'
  | 'training'
  | 'trainee_worker'
  | 'generic_worker'
  | 'active_worker'
  | 'rejected'
  | 'no_show'
  | 'blacklisted';

export type UpdatableProfileStatusCode = Exclude<
  ProfileStatusCode,
  'lead' | 'profile_completed'
>;

export type RevertSentinel = '__revert__';

export type ScoreLetter = 'A' | 'B' | 'C';

export type UserStatus = 'pending_verification' | 'pending_password' | 'active';

export type IncidentChangeType =
  | 'admin_update'
  | 'admin_avatar_update'
  | 'status_change'
  | 'video_uploaded'
  | 'video_replaced';

export interface ProfileStatusEntry {
  code: ProfileStatusCode;
  label: string;
  labelEs: string;
  labelEn: string;
  color: string;
  sortOrder: number;
}

// Lead profile catalogs (nationalities, genders, marital statuses).
// Nationalities are global; genders and marital statuses are per country, so
// their ids differ across countries.
export interface LeadProfileCatalogEntry {
  id: number;
  code: string;
  label: string;
  labelEs: string;
  labelEn: string;
}

// Catalog value resolved in the lead detail. Inactive rows are still returned
// here (e.g. the migrated gender `other`) even though the catalog omits them.
export interface LeadCatalogRef {
  id: number;
  code: string;
  name: string;
}

// Payment data ("Datos de pago"). Only countries with payment catalogs have it
// (today CL); elsewhere the detail returns `payment: null` and the grid
// `paymentStatus: 'not_applicable'`.
export type LeadPaymentStatus = 'not_applicable' | 'pending' | 'complete';

export type HealthProviderKind = 'public' | 'isapre';

// Payment catalogs share the profile catalog shape; health providers add
// `kind` (public = FONASA).
export interface LeadPaymentCatalogEntry extends LeadProfileCatalogEntry {
  kind?: HealthProviderKind;
}

export interface LeadPaymentDetail {
  status: 'pending' | 'complete';
  bank: LeadCatalogRef | null;
  accountType: LeadCatalogRef | null;
  // Full account number, digits only (sensitive: never in the grid).
  accountNumber: string | null;
  pensionFund: LeadCatalogRef | null;
  healthProvider: (LeadCatalogRef & { kind: HealthProviderKind }) | null;
  shipping: {
    // True when the residence comuna is outside the RM.
    required: boolean;
    sameAsResidence: boolean;
    street: string | null;
    number: string | null;
    unit: string | null;
    city: {
      id: number;
      name: string;
      regionId: string;
      regionName: string;
    } | null;
  };
  // Lead-facing: the admin view does not use it.
  missingFields: string[];
  updatedAt: string | null;
}

export interface ActiveShift {
  id: number;
  label: string;
  sortOrder: number;
}

export interface AdminCityRow {
  id: number;
  cityName: string;
  regionId: string;
  regionName: string;
  provinceId: string;
  provinceName: string;
  cityId: string;
  countryCode: string;
}

export interface AdminRegion {
  // varchar(10) — el zero-padding es significativo ("05" !== "5"): reenviar tal cual
  regionId: string;
  regionName: string;
  countryCode: string;
}

export interface AdminUniversity {
  id: number;
  name: string;
  sortOrder: number;
}

export const LEAD_TAGS_MAX_PER_LEAD = 20;
export const LEAD_TAG_NAME_MAX_LENGTH = 50;
/** Max tag ids per list/export filter; the server rejects more with a 400. */
export const LEAD_TAG_FILTER_MAX = 50;

export interface LeadTag {
  id: number;
  name: string;
}

export interface LeadTagWithCount extends LeadTag {
  leadCount: number;
}

export type LeadTagMatch = 'any' | 'all';

export interface BulkAssignLeadTagsInput {
  country: string;
  userIds: string[];
  tags: string[];
}

export interface BulkAssignLeadTagResult extends LeadTag {
  created: boolean;
  assignedCount: number;
  alreadyHadCount: number;
}

export interface BulkAssignLeadTagsResponse {
  tags: BulkAssignLeadTagResult[];
}

export interface LeadCard {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  statusCode: ProfileStatusCode;
  statusNameEs: string;
  statusNameEn: string;
  cityId: number | null;
  cityName: string | null;
  regionName: string | null;
  universityId: number | null;
  universityName: string | null;
  otherUniversity: string | null;
  career: string | null;
  createdAt: string;
  // Opcional hasta que el backend lo exponga: ausente equivale a [].
  tags?: LeadTag[];
  nationalityId?: number | null;
  nationalityName?: string | null;
  genderId?: number | null;
  genderName?: string | null;
  maritalStatusId?: number | null;
  maritalStatusName?: string | null;
}

export interface LeadsResponse {
  data: LeadCard[];
  page: number;
  pageSize: number;
  totalRows: number;
}

export interface AdminUserBasic {
  id: string;
  email: string;
  countryCode: string;
  status: UserStatus;
  createdAt: string;
  updatedAt: string;
}

export interface AdminProfile {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  phoneCountryCode: string | null;
  birthday: string | null;
  nationalityId?: number | null;
  rut: string | null;
  avatarUrl: string | null;
  address: string | null;
  cityId: number | null;
  universityId: number | null;
  otherUniversity: string | null;
  notStudying: boolean | null;
  career: string | null;
  statusCode: ProfileStatusCode;
  labelEs: string;
  labelEn: string;
  scoreProfile: number | null;
  scoreAptitude: ScoreLetter | null;
  scoreSales: ScoreLetter | null;
  hrNotes: string | null;
  documentExpirationDate: string | null;
  onboardingCompletedAt: string | null;
  availabilityLastUpdatedAt: string | null;
  introductionVideoAttempts: number;
  videoUploadedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ActiveVideo {
  id: string;
  r2VideoKey: string;
  r2ThumbnailKey: string;
  presignedVideoUrl: string;
  presignedThumbnailUrl: string;
  mimeType: string;
  sizeBytes: number;
  durationSeconds: number;
  uploadedAt: string;
  attempts: number;
  signedUrlExpiresAt: string;
}

export type LastStatusChange =
  | {
      kind: 'admin';
      adminId: number;
      adminFirstName: string | null;
      adminLastName: string | null;
      section: string;
      createdAt: string;
    }
  | {
      kind: 'system';
      source:
        | 'onboarding_complete'
        | 'video_auto_flip'
        | 'backfill_migration'
        | 'training_attended_auto';
      createdAt: string;
    };

export interface AdminIncident {
  id: string;
  changedBy: number | null;
  changeType: IncidentChangeType;
  fieldName: string | null;
  oldValue: string | null;
  newValue: string | null;
  snapshot: Record<string, unknown> | null;
  reason: string | null;
  createdAt: string;
}

export interface AdminUserDetail {
  user: AdminUserBasic;
  profile: AdminProfile;
  city: AdminCityRow | null;
  university: { id: number; name: string } | null;
  incidents: AdminIncident[];
  availabilityShifts: { id: number; label: string }[];
  activeVideo: ActiveVideo | null;
  lastStatusChange: LastStatusChange | null;
  // Opcional hasta que el backend lo exponga: ausente equivale a [].
  tags?: LeadTag[];
  // Opcionales hasta que el backend los exponga: ausente equivale a null.
  nationality?: LeadCatalogRef | null;
  gender?: LeadCatalogRef | null;
  maritalStatus?: LeadCatalogRef | null;
  // null: the lead's country has no payment data. Ausente equivale a null.
  payment?: LeadPaymentDetail | null;
}

export interface PatchProfileBasicInput {
  section: 'basic';
  firstName?: string;
  lastName?: string;
  phone?: string | null;
  phoneCountryCode?: string | null;
  birthday?: string | null;
  address?: string | null;
  cityId?: number | null;
  // Catalog ids; null clears, omitted leaves unchanged.
  nationalityId?: number | null;
  genderId?: number | null;
  maritalStatusId?: number | null;
}

export interface PatchProfileIdentityInput {
  section: 'identity';
  rut?: string | null;
  documentExpirationDate?: string | null;
}

export interface PatchProfileEvaluationInput {
  section: 'evaluation';
  scoreProfile?: number | null;
  scoreAptitude?: ScoreLetter | null;
  scoreSales?: ScoreLetter | null;
  hrNotes?: string | null;
  status?: UpdatableProfileStatusCode | RevertSentinel;
}

export interface PatchProfileOtherInput {
  section: 'other';
  universityId?: number | null;
  otherUniversity?: string | null;
  notStudying?: boolean | null;
  career?: string | null;
  shiftIds?: number[];
}

// Partial update: omitted keys stay unchanged, null clears.
export interface PatchProfilePaymentInput {
  section: 'payment';
  bankId?: number | null;
  accountTypeId?: number | null;
  // Digits; the server also accepts spaces and hyphens (4-20 digits).
  accountNumber?: string | null;
  pensionFundId?: number | null;
  healthProviderId?: number | null;
  shippingStreet?: string | null;
  shippingNumber?: string | null;
  shippingUnit?: string | null;
  shippingCityId?: number | null;
  shippingSameAsResidence?: boolean;
}

export type PatchProfileInput =
  | PatchProfileBasicInput
  | PatchProfileIdentityInput
  | PatchProfileEvaluationInput
  | PatchProfileOtherInput
  | PatchProfilePaymentInput;

export type StatusUpdateResult =
  | { applied: true; revertedTo?: string }
  | { applied: false; reason: 'invalid_target_state' | 'no_change' };

export interface PatchProfileResponse {
  ok: true;
  updatedProfile: AdminProfile;
  incidents: AdminIncident[];
  statusUpdate?: StatusUpdateResult;
}

// The payment section answers with the payment block, not the profile.
export interface PatchProfilePaymentResponse {
  ok: true;
  updatedProfile: LeadPaymentDetail;
  incidents: AdminIncident[];
}

export interface LeadsListParams {
  country: string;
  q?: string;
  states?: ProfileStatusCode[];
  universityId?: number | null;
  // `null` representa "Otros" (leads sin universidad del catálogo)
  universityIds?: (number | null)[];
  // regionId tal cual viene del catálogo — el padding importa
  regionIds?: string[];
  // `id` integer de GET /cities, NO el `cityId` varchar
  cityIds?: number[];
  // Ids de etiquetas; `tagMatch` sólo aplica si hay alguna.
  tagIds?: number[];
  tagMatch?: LeadTagMatch;
  // Catalog ids; OR within each filter, AND with the rest.
  nationalityIds?: number[];
  genderIds?: number[];
  maritalStatusIds?: number[];
  // true = complete, false = pending; omitted = all. not_applicable leads
  // match neither value.
  paymentConfigured?: boolean;
  page?: number;
  pageSize?: number;
  sort?: 'fullName' | 'createdAt' | 'statusCode';
  dir?: 'asc' | 'desc';
}

export interface IncidentsResponse {
  data: AdminIncident[];
}

export interface ExportLeadsParams {
  country: string;
  timezone?: string;
  createdFrom?: string;
  createdTo?: string;
  availabilityUpdatedFrom?: string;
  availabilityUpdatedTo?: string;
  statuses?: ProfileStatusCode[];
  // Mismos filtros y semántica que en el listado. A diferencia de `/leads`, el
  // export no tiene `universityId` singular: nace directamente múltiple.
  universityIds?: (number | null)[];
  regionIds?: string[];
  cityIds?: number[];
  tagIds?: number[];
  tagMatch?: LeadTagMatch;
}
